import React, { useCallback, useState, useEffect } from 'react';
import {
  Container,
  BotaoLimpar,
  BotaoImprimir,
  BotoesContainer,
  ContainerCheckbox,
  CirculoCarregando,
  CirculoCarregandoDiv,
} from './styles';
import { FiFileText, FiPrinter, FiRefreshCw } from 'react-icons/fi';

import { InputData } from '../../Paciente/styles';
import ComboboxComFiltro from '../../../components/autocomplete';

import api from '../../../services/api';
import IProfissional from '../../../routes/vms/IProfissional';

import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { downloadReport } from '../../../utils/downloadReport';
import { downloadExcel } from '../../../utils/downloadExcel';

const OpcoesTipo = [
  { id: 'RESUMIDO', nome: 'RESUMIDO' },
  { id: 'DETALHADO', nome: 'DETALHADO' },
];

export const FechamentoCategoria: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [profissionais, setProfissionais] = useState<IProfissional[]>([]);

  const [dataIni, setDataIni] = useState('');
  const [dataIniErro, setDataIniErro] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dataFimErro, setDataFimErro] = useState('');
  const [profissional, setProfissional] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [tipoErro, setTipoErro] = useState('');
  const [exibeCusto, setExibeCusto] = useState(false);
  const [exibePreco, setExibePreco] = useState(false);
  const [exibeLucro, setExibeLucro] = useState(false);
  const [exibeVrHora, setExibeVrHora] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const aoAlterarDataIni = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDataIni(event.target.value);
    setDataIniErro('');
  };

  const aoAlterarDataFim = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDataFim(event.target.value);
    setDataFimErro('');
  };

  const aoAlterarProfissional = (novoProfissional: string | null) => {
    setProfissional(novoProfissional);
  };

  const aoAlterarTipo = (novoTipo: string | null) => {
    setTipo(novoTipo);
    setTipoErro('');
  };

  const carregaProfissionais = useCallback(async () => {
    const response = await api.get<IProfissional[]>(
      `/users/profissionais?empresa_id=${user.empresa_id}`,
    );

    setProfissionais(response.data);
  }, [user.empresa_id]);

  const aoLimpar = useCallback(() => {
    setDataIni('');
    setDataIniErro('');
    setDataFim('');
    setDataFimErro('');
    setProfissional(null);
    setExibePreco(false);
    setExibeCusto(false);
    setExibeLucro(false);
    setTipo(null);
    setTipoErro('');
  }, []);

  const handleDownloadExcel = useCallback(async () => {
    setLoading(true);

    const response = await api.get('/agendamentos/fechamentos', {
      params: {
        dataIni,
        dataFim,
        profissionalId: profissional,
      },
    });

    const baixou = await downloadExcel({
      dados: {
        fechamento: response.data,
        exibeCusto,
        exibePreco,
        exibeLucro,
        exibeVrHora,
        resumido: tipo === 'RESUMIDO',
      },
      reportName: `Fechamento${new Date().getTime()}`,
      template: '/clinica/FechamentoCategoriaExcel/fechamento-excel',
    });

    if (!baixou) {
      addToast({
        title: 'Falha no Download',
        description: 'Não foi possível concluir o download',
        type: 'error',
      });

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }, [
    addToast,
    dataIni,
    dataFim,
    profissional,
    exibeCusto,
    exibePreco,
    exibeLucro,
    exibeVrHora,
    tipo,
  ]);

  const handleDownloadReport = useCallback(async () => {
    setLoading(true);

    const response = await api.get('/agendamentos/fechamentos', {
      params: {
        dataIni,
        dataFim,
        profissionalId: profissional,
      },
    });

    const baixou = await downloadReport({
      dados: {
        fechamento: response.data,
        profissional,
        resumido: tipo === 'RESUMIDO',
        exibePreco,
        exibeCusto,
        exibeLucro,
        exibeVrHora,
        dataIni,
        dataFim,
      },
      reportName: `Fechamento${new Date().getTime()}`,
      template: '/clinica/FechamentoCategoria/fechamento-main',
    });

    if (!baixou) {
      addToast({
        title: 'Falha no Download',
        description: 'Não foi possível concluir o download',
        type: 'error',
      });

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }, [
    addToast,
    dataIni,
    dataFim,
    exibePreco,
    exibeCusto,
    exibeLucro,
    exibeVrHora,
    profissional,
    tipo,
  ]);

  const aoImprimir = useCallback(() => {
    setDataIniErro('');
    setDataFimErro('');
    setTipoErro('');

    if (dataIni === '') {
      setDataIniErro('Informação obrigatória!');
      return;
    }

    if (dataFim === '') {
      setDataFimErro('Informação obrigatória!');
      return;
    }

    if (!tipo) {
      setTipoErro('Informação obrigatória!');
      return;
    }

    handleDownloadReport();
  }, [dataIni, dataFim, tipo, handleDownloadReport]);

  useEffect(() => {
    carregaProfissionais();
  }, [carregaProfissionais]);

  return loading ? (
    <CirculoCarregandoDiv>
      <CirculoCarregando />
    </CirculoCarregandoDiv>
  ) : (
    <Container>
      <span>
        <InputData
          variant="outlined"
          margin="normal"
          label="Data Inicial"
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
          value={dataIni}
          onChange={aoAlterarDataIni}
          error={dataIniErro !== ''}
          helperText={dataIniErro}
          style={{ flex: 1 }}
        />

        <InputData
          variant="outlined"
          margin="normal"
          label="Data Final"
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
          value={dataFim}
          onChange={aoAlterarDataFim}
          error={dataFimErro !== ''}
          helperText={dataFimErro}
          style={{ flex: 1 }}
        />

        <ComboboxComFiltro
          opcoes={profissionais}
          value={profissional}
          label="Profissional"
          onChange={aoAlterarProfissional}
          size={3}
        />

        <ComboboxComFiltro
          opcoes={OpcoesTipo}
          value={tipo}
          label="Tipo"
          onChange={aoAlterarTipo}
          error={tipoErro !== ''}
          helperText={tipoErro}
          size={1.1}
        />

        <ContainerCheckbox>
          <label>
            <input
              type="checkbox"
              checked={exibeCusto}
              onChange={() => {
                setExibeCusto(!exibeCusto);
              }}
            />
            Custo
          </label>

          <label>
            <input
              type="checkbox"
              checked={exibePreco}
              onChange={() => {
                setExibePreco(!exibePreco);
              }}
            />
            Preço
          </label>

          <label>
            <input
              type="checkbox"
              checked={exibeLucro}
              onChange={() => {
                setExibeLucro(!exibeLucro);
              }}
            />
            Lucro
          </label>
        </ContainerCheckbox>

        <ContainerCheckbox>
          <label>
            <input
              type="checkbox"
              checked={exibeVrHora}
              onChange={() => {
                setExibeVrHora(!exibeVrHora);
              }}
            />
            Vr. Hora
          </label>
        </ContainerCheckbox>
      </span>

      <BotoesContainer>
        <BotaoImprimir onClick={aoImprimir}>
          <FiPrinter />
          Imprimir
        </BotaoImprimir>

        <BotaoImprimir onClick={handleDownloadExcel}>
          <FiFileText />
          Excel
        </BotaoImprimir>

        <BotaoLimpar onClick={aoLimpar}>
          <FiRefreshCw />
          Limpar
        </BotaoLimpar>
      </BotoesContainer>
    </Container>
  );
};
